// Pestanya Contactes d'un Treball: gestiona la relació ContactesTreballs
// (rol, notes, preferent, vincle, alta, desvinculació).
// No edita nom/telèfon/email del Contacte. Avui l'alta es fa des de diversos
// punts i no hi ha cap pantalla d'edició; quan n'existeixi una de comuna,
// obrir-la des d'aquí en lloc d'afegir un CRUD local a cada alta.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { API_URL } from "../../utils/config";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { formatAddress } from "../../utils/Utilitats";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import AjudaTreball from "./AjudaTreball";

const ADRECA_BUIDA = {
  TipusVia: "",
  NomVia: "",
  NumVia: "",
  EscalaVia: "",
  Pis: "",
  Porta: "",
  Poblacio: "",
  Provincia: "",
  Pais: "Espanya",
  CodiPostal: "",
  Referencies: "",
};

function idRelacio(c) {
  return c ? Number(c.idContactesTreballs) : null;
}

function esEntitatVinculada(c) {
  return !!(c && c.Entitats_idEntitats && !c.Contactes_idContactes);
}

function esRolClient(rol) {
  return String(rol || "").trim().toUpperCase() === "CLIENT";
}

function teAdreca(c) {
  if (!c) return false;
  return !!(c.NomVia || c.Poblacio || c.Adreces_idAdreces);
}

function adrecaDe(c) {
  if (!c) return { ...ADRECA_BUIDA };
  return {
    TipusVia: c.TipusVia || "",
    NomVia: c.NomVia || "",
    NumVia: c.NumVia || "",
    EscalaVia: c.EscalaVia || "",
    Pis: c.Pis || "",
    Porta: c.Porta || "",
    Poblacio: c.Poblacio || "",
    Provincia: c.Provincia || "",
    Pais: c.Pais || "Espanya",
    CodiPostal: c.CodiPostal || "",
    Referencies: c.Referencies || "",
  };
}

function nomContacte(c) {
  if (!c) return "—";
  const cognoms = [c.Cognom1, c.Cognom2].filter((x) => x && String(x).trim()).join(" ");
  const nom = [cognoms, c.Nom].filter((x) => x && String(x).trim()).join(", ");
  return nom || c.NomMostrar || "—";
}

function ContactesTreball({ treball, onCancel, subnav }) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const potCrear = tincPermis(rolUsuari, "CREAR") || tincPermis(rolUsuari, "MODIFICAR");
  const potConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const potModificar = tincPermis(rolUsuari, "MODIFICAR");
  const idEmpresa = empresaSeleccionada?.IdEmpresa;
  const idTreballs = treball?.idTreballs;

  const [activeTab, setActiveTab] = useState("table");
  const [contactes, setContactes] = useState([]);
  const [totsContactes, setTotsContactes] = useState([]);
  const [entitatsEmpresa, setEntitatsEmpresa] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [enviant, setEnviant] = useState(false);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const [filtreExistent, setFiltreExistent] = useState("");
  const [idExistent, setIdExistent] = useState("");
  const [rolNou, setRolNou] = useState("");
  const [notesNou, setNotesNou] = useState("");
  const [preferentNou, setPreferentNou] = useState(false);
  const [vincularEntitat, setVincularEntitat] = useState(false);
  const [nom, setNom] = useState("");
  const [cognom1, setCognom1] = useState("");
  const [cognom2, setCognom2] = useState("");
  const [telefon, setTelefon] = useState("");
  const [email, setEmail] = useState("");
  const [indicarAdreca, setIndicarAdreca] = useState(false);
  const [adreca, setAdreca] = useState({ ...ADRECA_BUIDA });
  const [dups, setDups] = useState(null);

  const [rolEdit, setRolEdit] = useState("");
  const [notesEdit, setNotesEdit] = useState("");
  const [preferentEdit, setPreferentEdit] = useState(false);

  const handleEsc = useCallback(() => {
    if (activeTab === "table") onCancel?.();
    else {
      setActiveTab("table");
      setError("");
      setDups(null);
      setMenuAccions(false);
    }
  }, [activeTab, onCancel]);
  TeclaEscEnter(handleEsc, null);

  const carregar = useCallback(async () => {
    if (!idTreballs) return [];
    try {
      const resp = await fetch(`${API_URL}/ContactesTreball.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idTreballs, idEmpresa }),
      });
      const data = await resp.json();
      if (data.success) {
        const llista = data.contactes || [];
        setContactes(llista);
        return llista;
      }
      setError(data.message || "No s'han pogut carregar els contactes.");
      setContactes([]);
      return [];
    } catch (e) {
      setError("Error de connexió amb el servidor.");
      setContactes([]);
      return [];
    }
  }, [idTreballs, idEmpresa]);

  const carregarTots = useCallback(async () => {
    if (!idEmpresa) return;
    try {
      const resp = await fetch(`${API_URL}/Contactes.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idEmpresa }),
      });
      const data = await resp.json();
      if (data.success) setTotsContactes(data.contactes || []);
    } catch (e) {
      /* la cerca queda buida */
    }
  }, [idEmpresa]);

  const carregarEntitats = useCallback(async () => {
    if (!idEmpresa) return;
    try {
      const resp = await fetch(`${API_URL}/Entitatsempresa.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idEmpresa }),
      });
      const data = await resp.json();
      if (data.success) setEntitatsEmpresa(data.entitats || []);
    } catch (e) {
      /* la cerca d'entitats queda buida */
    }
  }, [idEmpresa]);

  useEffect(() => {
    carregar();
  }, [carregar]);
  useEffect(() => {
    carregarTots();
    carregarEntitats();
  }, [carregarTots, carregarEntitats]);

  useEffect(() => {
    setSelected((prev) => {
      if (!prev) return prev;
      return contactes.find((c) => idRelacio(c) === idRelacio(prev)) || prev;
    });
  }, [contactes]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  useEffect(() => {
    if (activeTab !== "consultar") setMenuAccions(false);
  }, [activeTab]);

  const personesDisponibles = useMemo(() => {
    const idsContacte = new Set(contactes.map((c) => Number(c.Contactes_idContactes)).filter(Boolean));
    const idsEntitat = new Set(contactes.map((c) => Number(c.Entitats_idEntitats)).filter(Boolean));
    const q = filtreExistent.trim().toLowerCase();
    const llista = [
      ...totsContactes
        .filter((c) => !idsContacte.has(Number(c.idContactes)))
        .map((c) => ({ ...c, clau: `c:${c.idContactes}`, tipus: "contacte" })),
      ...entitatsEmpresa
        .filter((e) => !idsEntitat.has(Number(e.idEntitats)))
        .map((e) => ({ ...e, clau: `e:${e.idEntitats}`, tipus: "entitat" })),
    ];
    return llista
      .filter((p) => {
        if (p.clau === idExistent) return true;
        if (!q) return true;
        const blob = `${p.NomMostrar || ""} ${p.Nom || ""} ${p.Cognom1 || ""} ${p.Telefon || ""} ${p.Email || ""} ${p.Papers || ""}`.toLowerCase();
        return blob.includes(q);
      })
      .sort((a, b) => String(a.NomMostrar || a.Nom || "").localeCompare(String(b.NomMostrar || b.Nom || ""), "ca"));
  }, [totsContactes, entitatsEmpresa, contactes, filtreExistent, idExistent]);

  const existentSeleccionat = useMemo(
    () => personesDisponibles.find((p) => p.clau === idExistent) || null,
    [personesDisponibles, idExistent]
  );

  const resetNou = () => {
    setFiltreExistent("");
    setIdExistent("");
    setRolNou("");
    setNotesNou("");
    setPreferentNou(false);
    setVincularEntitat(false);
    setNom("");
    setCognom1("");
    setCognom2("");
    setTelefon("");
    setEmail("");
    setIndicarAdreca(false);
    setAdreca({ ...ADRECA_BUIDA });
    setDups(null);
    setError("");
  };

  const omplirDesDeExistent = (id) => {
    const c = personesDisponibles.find((x) => x.clau === id)
      || totsContactes.find((x) => `c:${x.idContactes}` === id)
      || entitatsEmpresa.find((x) => `e:${x.idEntitats}` === id);
    if (!c) {
      setNom("");
      setCognom1("");
      setCognom2("");
      setTelefon("");
      setEmail("");
      setIndicarAdreca(false);
      setAdreca({ ...ADRECA_BUIDA });
      return;
    }
    setNom(c.Nom || "");
    setCognom1(c.Cognom1 || "");
    setCognom2(c.Cognom2 || "");
    setTelefon(c.Telefon || "");
    setEmail(c.Email || "");
    setAdreca(adrecaDe(c));
    setIndicarAdreca(teAdreca(c));
  };

  const anarATaula = async () => {
    setActiveTab("table");
    setError("");
    setDups(null);
    setMenuAccions(false);
    await carregar();
    await carregarTots();
    await carregarEntitats();
  };

  const postJson = async (endpoint, body) => {
    const resp = await fetch(`${API_URL}/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return resp.json();
  };

  const enviarVincle = async (payloadExtra = {}) => {
    setEnviant(true);
    setError("");
    try {
      const data = await postJson("ContactesTreballVincular.php", {
        idEmpresa,
        idTreballs,
        RolTreball: rolNou,
        Notes: notesNou,
        ContactePreferent: preferentNou ? 1 : 0,
        VincularEntitatClient: vincularEntitat ? 1 : 0,
        ...payloadExtra,
      });
      if (!data.success) {
        setError(data.message || "No s'ha pogut vincular el contacte.");
        return;
      }
      resetNou();
      setSelected(null);
      await anarATaula();
    } catch (e) {
      setError("Error de connexió amb el servidor.");
    } finally {
      setEnviant(false);
    }
  };

  const handleVincularExistent = async () => {
    const persona = personesDisponibles.find((p) => p.clau === idExistent);
    if (!persona) {
      setError("Selecciona un contacte o una entitat.");
      return;
    }
    if (persona.tipus === "entitat") {
      await enviarVincle({ idEntitats: Number(persona.idEntitats) });
    } else {
      await enviarVincle({ idContactes: Number(persona.idContactes) });
    }
  };

  const buscarDuplicats = async () => {
    const resp = await fetch(`${API_URL}/Buscarcontacte.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idEmpresa, nom, cognom1, telefon, email }),
    });
    const data = await resp.json();
    if (data.success && data.contactes?.length) return data.contactes;
    return null;
  };

  const handleCrear = async (ignorarDups = false) => {
    if (!nom.trim()) {
      setError("Cal el nom del contacte nou.");
      return;
    }
    if (!ignorarDups) {
      const trobats = await buscarDuplicats();
      if (trobats) {
        setDups(trobats);
        return;
      }
    }
    await enviarVincle({
      ContacteNou: {
        Nom: nom,
        Cognom1: cognom1,
        Cognom2: cognom2,
        Telefon: telefon,
        Email: email,
        indicarAdreca,
        adreca: indicarAdreca ? adreca : null,
      },
    });
  };

  const handleGuardarRelacio = async () => {
    if (!selected) return;
    setEnviant(true);
    setError("");
    try {
      const data = await postJson("ContactesTreballRelacio.php", {
        idEmpresa,
        idTreballs,
        idContactesTreballs: selected.idContactesTreballs,
        RolTreball: rolEdit,
        Notes: notesEdit,
        ContactePreferent: preferentEdit ? 1 : 0,
        VincularEntitatClient: esRolClient(rolEdit) && vincularEntitat ? 1 : 0,
      });
      if (!data.success) setError(data.message || "No s'ha pogut actualitzar.");
      else {
        setSelected(null);
        await anarATaula();
      }
    } catch (e) {
      setError("Error de connexió amb el servidor.");
    } finally {
      setEnviant(false);
    }
  };

  const handleDesvincular = async () => {
    if (!selected) return;
    setEnviant(true);
    setError("");
    try {
      const data = await postJson("ContactesTreballDesvincular.php", {
        idEmpresa,
        idTreballs,
        idContactesTreballs: selected.idContactesTreballs,
      });
      if (!data.success) setError(data.message || "No s'ha pogut desvincular.");
      else {
        setSelected(null);
        await anarATaula();
      }
    } catch (e) {
      setError("Error de connexió amb el servidor.");
    } finally {
      setEnviant(false);
    }
  };

  const obrirNou = () => {
    resetNou();
    setSelected(null);
    setActiveTab("nou");
  };

  const obrirFitxa = (c, mode) => {
    setMenuObert(null);
    setSelected(c);
    setError("");
    if (mode === "modificar") {
      setRolEdit(c.RolTreball || "");
      setNotesEdit(c.Notes || "");
      setPreferentEdit(Number(c.ContactePreferent) === 1);
      setVincularEntitat(false);
    }
    setActiveTab(mode);
  };

  const canNavigate = contactes.length > 1 && !!selected;
  const idxConsultar = selected
    ? contactes.findIndex((c) => idRelacio(c) === idRelacio(selected))
    : -1;

  const goPrev = () => {
    if (!selected) return;
    const idx = contactes.findIndex((c) => idRelacio(c) === idRelacio(selected));
    if (idx < 0) return;
    const prev = idx === 0 ? contactes.length - 1 : idx - 1;
    setSelected(contactes[prev]);
  };
  const goNext = () => {
    if (!selected) return;
    const idx = contactes.findIndex((c) => idRelacio(c) === idRelacio(selected));
    if (idx < 0) return;
    const next = idx === contactes.length - 1 ? 0 : idx + 1;
    setSelected(contactes[next]);
  };

  const obrirMenu = (e, c) => {
    e.stopPropagation();
    const id = idRelacio(c);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 160;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const camp = (label, content, mida = "mig") => {
    const extra =
      mida === "ample"
        ? "dv2-field--ample"
        : mida === "llarg"
        ? "dv2-field--llarg"
        : mida === "curt"
        ? "dv2-field--curt"
        : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className="dv2-label">{label}</span>
        {content}
      </label>
    );
  };

  const valor = (txt) => <span className="dv2-value">{txt || "—"}</span>;

  const xips = (c) => (
    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
      {Number(c.ContactePreferent) === 1 && <span className="dv2-chip dv2-chip--ok">Preferent</span>}
      {Number(c.VinculatEntitatClient) === 1 && <span className="dv2-chip">Client</span>}
    </span>
  );

  const renderAccions = (c) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idRelacio(c) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, c)}
      >
        …
      </button>
    </div>
  );

  const contacteMenu = menuObert ? contactes.find((c) => idRelacio(c) === menuObert) : null;

  const renderTaula = () => (
    <section className="dv2-card">
      {contactes.length === 0 ? (
        <p className="dv2-empty">
          Aquest treball no té cap contacte vinculat. Les dades de contacte de la fitxa provenen del client.
        </p>
      ) : isNarrow ? (
        <div className="dv2-recordList" style={{ display: "flex" }}>
          {contactes.map((c) => {
            const isSelected = idRelacio(selected) === idRelacio(c);
            return (
              <article
                key={idRelacio(c)}
                className={`dv2-record ${isSelected ? "is-selected" : ""}`}
                onClick={() => setSelected(isSelected ? null : c)}
                onDoubleClick={() => potConsultar && obrirFitxa(c, "consultar")}
              >
                <div>
                  <p className="dv2-record-title">{nomContacte(c)}</p>
                  <p className="dv2-record-meta">
                    {[c.RolTreball, c.Telefon, c.Email].filter(Boolean).join(" · ") || "—"}
                  </p>
                  {xips(c)}
                </div>
                {renderAccions(c)}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="dv2-tableWrap">
          <table className="dv2-table">
            <thead>
              <tr>
                <th>Contacte</th>
                <th>Telèfon</th>
                <th>Email</th>
                <th>Rol al treball</th>
                <th>Notes</th>
                <th></th>
                <th className="dv2-table-actions">Accions</th>
              </tr>
            </thead>
            <tbody>
              {contactes.map((c) => {
                const isSelected = idRelacio(selected) === idRelacio(c);
                return (
                  <tr
                    key={idRelacio(c)}
                    className={isSelected ? "is-selected" : ""}
                    onClick={() => setSelected(isSelected ? null : c)}
                    onDoubleClick={() => potConsultar && obrirFitxa(c, "consultar")}
                  >
                    <td>{nomContacte(c)}</td>
                    <td>{c.Telefon || "—"}</td>
                    <td>{c.Email || "—"}</td>
                    <td>{c.RolTreball || "—"}</td>
                    <td className="dv2-cell-clip" title={c.Notes || ""}>
                      {c.Notes || "—"}
                    </td>
                    <td>{xips(c)}</td>
                    <td className="dv2-table-actions">{renderAccions(c)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {contacteMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {potConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(contacteMenu, "consultar")}>
                Consultar
              </button>
            )}
            {potModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(contacteMenu, "modificar")}>
                Modificar
              </button>
            )}
            {potModificar && (
              <button
                type="button"
                className="dv2-menu-item is-danger"
                onClick={() => obrirFitxa(contacteMenu, "desvincular")}
              >
                Desvincular
              </button>
            )}
          </div>,
          document.body
        )}
    </section>
  );

  const campsIdentitat = (c, editable) => {
    const ro = !editable;
    return (
      <>
        {camp(
          "Nom",
          ro ? (
            valor(c?.Nom)
          ) : (
            <input className="dv2-input" type="text" value={nom} onChange={(e) => setNom(e.target.value)} />
          )
        )}
        {camp(
          "Cognom 1",
          ro ? (
            valor(c?.Cognom1)
          ) : (
            <input className="dv2-input" type="text" value={cognom1} onChange={(e) => setCognom1(e.target.value)} />
          )
        )}
        {camp(
          "Cognom 2",
          ro ? (
            valor(c?.Cognom2)
          ) : (
            <input className="dv2-input" type="text" value={cognom2} onChange={(e) => setCognom2(e.target.value)} />
          )
        )}
        <span className="dv2-fields-break" aria-hidden="true" />
        {camp(
          "Telèfon",
          ro ? (
            valor(c?.Telefon)
          ) : (
            <input className="dv2-input" type="text" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
          )
        )}
        {camp(
          "Email",
          ro ? (
            valor(c?.Email)
          ) : (
            <input className="dv2-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          ),
          "llarg"
        )}
      </>
    );
  };

  const campsAdrecaNou = () => (
    <>
      <span className="dv2-fields-break" aria-hidden="true" />
      {camp(
        "Indicar adreça",
        <span className="dv2-toggleRow">
          <span className="dv2-toggle">
            <input type="checkbox" checked={indicarAdreca} onChange={(e) => setIndicarAdreca(e.target.checked)} />
            <span className="dv2-toggle-track" />
          </span>
        </span>,
        "curt"
      )}
      {indicarAdreca && (
        <>
          {camp(
            "Tipus de via",
            <select
              className="dv2-select"
              value={adreca.TipusVia}
              onChange={(e) => setAdreca((p) => ({ ...p, TipusVia: e.target.value }))}
            >
              <option value="">Selecciona…</option>
              {Object.keys(mapejos.tipusVia || {}).map((k) => (
                <option key={k} value={k}>
                  {mapToPresentable(k, mapejos.tipusVia)}
                </option>
              ))}
            </select>
          )}
          {camp(
            "Nom de la via",
            <input
              className="dv2-input"
              value={adreca.NomVia}
              maxLength={100}
              onChange={(e) => setAdreca((p) => ({ ...p, NomVia: e.target.value }))}
            />,
            "llarg"
          )}
          {camp(
            "Número",
            <input
              className="dv2-input"
              value={adreca.NumVia}
              maxLength={15}
              onChange={(e) => setAdreca((p) => ({ ...p, NumVia: e.target.value }))}
            />,
            "curt"
          )}
          {camp(
            "Escala",
            <input
              className="dv2-input"
              value={adreca.EscalaVia}
              maxLength={10}
              onChange={(e) => setAdreca((p) => ({ ...p, EscalaVia: e.target.value }))}
            />,
            "curt"
          )}
          {camp(
            "Pis",
            <input
              className="dv2-input"
              value={adreca.Pis}
              maxLength={25}
              onChange={(e) => setAdreca((p) => ({ ...p, Pis: e.target.value }))}
            />,
            "curt"
          )}
          {camp(
            "Porta",
            <input
              className="dv2-input"
              value={adreca.Porta}
              maxLength={5}
              onChange={(e) => setAdreca((p) => ({ ...p, Porta: e.target.value }))}
            />,
            "curt"
          )}
          <span className="dv2-fields-break" aria-hidden="true" />
          {camp(
            "Població",
            <input
              className="dv2-input"
              value={adreca.Poblacio}
              maxLength={100}
              onChange={(e) => setAdreca((p) => ({ ...p, Poblacio: e.target.value }))}
            />,
            "llarg"
          )}
          {camp(
            "Província",
            <input
              className="dv2-input"
              value={adreca.Provincia}
              maxLength={50}
              onChange={(e) => setAdreca((p) => ({ ...p, Provincia: e.target.value }))}
            />
          )}
          {camp(
            "País",
            <input
              className="dv2-input"
              value={adreca.Pais}
              maxLength={100}
              onChange={(e) => setAdreca((p) => ({ ...p, Pais: e.target.value }))}
            />
          )}
          {camp(
            "Codi postal",
            <input
              className="dv2-input"
              value={adreca.CodiPostal}
              maxLength={10}
              onChange={(e) => setAdreca((p) => ({ ...p, CodiPostal: e.target.value }))}
            />,
            "curt"
          )}
          {camp(
            "Referències",
            <textarea
              className="dv2-textarea"
              value={adreca.Referencies}
              maxLength={256}
              onChange={(e) => setAdreca((p) => ({ ...p, Referencies: e.target.value }))}
            />,
            "ample"
          )}
        </>
      )}
    </>
  );

  const campsRelacio = (rol, setRol, notes, setNotes, preferent, setPreferent, { editable, mostrarVincle }) => (
    <>
      {camp(
        "Rol al treball",
        editable ? (
          <input
            className="dv2-input"
            type="text"
            value={rol}
            onChange={(e) => setRol(e.target.value)}
            placeholder="Ex: CLIENT, cap d'obra, electricista…"
            disabled={enviant}
          />
        ) : (
          valor(rol)
        ),
        "llarg"
      )}
      {camp(
        "Notes",
        editable ? (
          <input
            className="dv2-input"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={enviant}
          />
        ) : (
          valor(notes)
        ),
        "ample"
      )}
      {camp(
        "Contacte preferent",
        editable ? (
          <span className="dv2-toggleRow">
            <span className="dv2-toggle">
              <input
                type="checkbox"
                checked={preferent}
                disabled={enviant}
                onChange={(e) => setPreferent(e.target.checked)}
              />
              <span className="dv2-toggle-track" />
            </span>
          </span>
        ) : (
          valor(preferent ? "Sí" : "No")
        ),
        "curt"
      )}
      {mostrarVincle && esRolClient(rol) && (
        camp(
          "Vincular al client",
          <span className="dv2-toggleRow">
            <span className="dv2-toggle">
              <input
                type="checkbox"
                checked={vincularEntitat}
                disabled={enviant}
                onChange={(e) => setVincularEntitat(e.target.checked)}
              />
              <span className="dv2-toggle-track" />
            </span>
          </span>,
          "llarg"
        )
      )}
    </>
  );

  const renderNou = () => {
    const esExistent = !!idExistent;
    return (
      <>
        <div className="dv2-entity-body">
          {error ? <p className="dv2-hint">{error}</p> : null}
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">1</span>
              <h2 className="dv2-card-title">Contacte</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {camp(
                "Cerca existent",
                <input
                  className="dv2-input"
                  type="text"
                  value={filtreExistent}
                  onChange={(e) => setFiltreExistent(e.target.value)}
                  placeholder="Nom, telèfon o email"
                />
              )}
              {camp(
                "Contacte existent",
                <select
                  className="dv2-select"
                  value={idExistent}
                  onChange={(e) => {
                    const id = e.target.value;
                    setIdExistent(id);
                    omplirDesDeExistent(id);
                  }}
                >
                  <option value="">— Crear-ne un de nou —</option>
                  {personesDisponibles.map((c) => (
                    <option key={c.clau} value={c.clau}>
                      {nomContacte(c)}
                      {c.Papers ? ` · ${c.Papers}` : ""}
                      {c.Telefon ? ` · ${c.Telefon}` : ""}
                      {c.Email ? ` · ${c.Email}` : ""}
                    </option>
                  ))}
                </select>,
                "llarg"
              )}
              <span className="dv2-fields-break" aria-hidden="true" />
              {campsIdentitat(existentSeleccionat, !esExistent)}
              {esExistent
                ? teAdreca(existentSeleccionat) &&
                  camp("Adreça", valor(formatAddress(existentSeleccionat)), "ample")
                : campsAdrecaNou()}
            </div>
          </section>
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">2</span>
              <h2 className="dv2-card-title">Relació al treball</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {campsRelacio(rolNou, setRolNou, notesNou, setNotesNou, preferentNou, setPreferentNou, {
                editable: true,
                mostrarVincle: existentSeleccionat?.tipus !== "entitat",
              })}
            </div>
          </section>
        </div>
        <div className="dv2-entity-dock">
          <div className="dv2-footerActions">
            <button
              type="button"
              className="dv2-btn"
              onClick={() => {
                resetNou();
                setActiveTab("table");
              }}
              disabled={enviant}
            >
              Cancel·lar
            </button>
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={esExistent ? handleVincularExistent : () => handleCrear(false)}
              disabled={enviant}
            >
              {esExistent ? "Vincular" : "Crear i vincular"}
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderConsultar = () => {
    if (!selected) return null;
    const adrecaTxt = teAdreca(selected) ? formatAddress(selected) : "";
    return (
      <>
        <div className="dv2-entity-body">
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">1</span>
              <h2 className="dv2-card-title">Contacte</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {camp("Nom", valor(nomContacte(selected)), "llarg")}
              {camp("Telèfon", valor(selected.Telefon))}
              {camp("Email", valor(selected.Email), "llarg")}
              {adrecaTxt && adrecaTxt !== "—" ? camp("Adreça", valor(adrecaTxt), "ample") : null}
              <div className="dv2-field dv2-field--ample">{xips(selected)}</div>
            </div>
          </section>
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">2</span>
              <h2 className="dv2-card-title">Relació al treball</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {campsRelacio(
                selected.RolTreball,
                null,
                selected.Notes,
                null,
                Number(selected.ContactePreferent) === 1,
                null,
                { editable: false, mostrarVincle: false }
              )}
            </div>
          </section>
        </div>
        {canNavigate && (
          <div className="dv2-entity-dock">
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={goPrev} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {idxConsultar >= 0 ? idxConsultar + 1 : 0} / {contactes.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={goNext} title="Següent">
                ›
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderModificar = () => {
    if (!selected) return null;
    const adrecaTxt = teAdreca(selected) ? formatAddress(selected) : "";
    return (
      <>
        <div className="dv2-entity-body">
          {error ? <p className="dv2-hint">{error}</p> : null}
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">1</span>
              <h2 className="dv2-card-title">Contacte</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {camp("Nom", valor(nomContacte(selected)), "llarg")}
              {camp("Telèfon", valor(selected.Telefon))}
              {camp("Email", valor(selected.Email), "llarg")}
              {adrecaTxt && adrecaTxt !== "—" ? camp("Adreça", valor(adrecaTxt), "ample") : null}
              <p className="dv2-hint" style={{ flex: "1 1 100%" }}>
                Nom, telèfon i email no s'editen des d'aquí. Aquesta pantalla gestiona la relació amb el treball.
              </p>
            </div>
          </section>
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">2</span>
              <h2 className="dv2-card-title">Relació al treball</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {campsRelacio(rolEdit, setRolEdit, notesEdit, setNotesEdit, preferentEdit, setPreferentEdit, {
                editable: true,
                mostrarVincle: !esEntitatVinculada(selected),
              })}
            </div>
          </section>
        </div>
        <div className="dv2-entity-dock">
          <div className="dv2-footerActions">
            <button type="button" className="dv2-btn" onClick={() => setActiveTab("table")} disabled={enviant}>
              Cancel·lar
            </button>
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleGuardarRelacio} disabled={enviant}>
              Desar
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderDesvincular = () => {
    if (!selected) return null;
    return (
      <>
        <div className="dv2-banner dv2-banner--danger">
          Es treu del treball. El contacte global es conserva (pot ser a altres treballs o al client).
        </div>
        <div className="dv2-entity-body">
          {error ? <p className="dv2-hint">{error}</p> : null}
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">1</span>
              <h2 className="dv2-card-title">Contacte</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {camp("Nom", valor(nomContacte(selected)), "llarg")}
              {camp("Rol al treball", valor(selected.RolTreball), "llarg")}
              {camp("Notes", valor(selected.Notes), "ample")}
            </div>
          </section>
        </div>
        <div className="dv2-entity-dock">
          <div className="dv2-footerActions">
            <button type="button" className="dv2-btn" onClick={() => setActiveTab("table")} disabled={enviant}>
              Cancel·lar
            </button>
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleDesvincular} disabled={enviant}>
              Desvincular
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderFitxa = () => {
    switch (activeTab) {
      case "nou":
        return renderNou();
      case "consultar":
        return renderConsultar();
      case "modificar":
        return renderModificar();
      case "desvincular":
        return renderDesvincular();
      default:
        return null;
    }
  };

  if (!idTreballs) {
    return <p className="dv2-empty">Selecciona un treball per gestionar-ne els contactes.</p>;
  }

  const titolContactes =
    activeTab === "nou"
      ? "Nou contacte"
      : activeTab === "consultar"
      ? "Contacte del treball"
      : activeTab === "modificar"
      ? "Modificar relació"
      : activeTab === "desvincular"
      ? "Desvincular contacte"
      : "Contactes del treball";

  const subtitol =
    `${treball?.idTreballIntern || treball?.idTreballs || ""}${treball?.NomClient ? ` · ${treball.NomClient}` : ""}`.trim() ||
    "Persones útils per coordinar l'obra: cap d'obra, ofici, prevenció…";

  return (
    <div className="dv2-entity">
      {mostraAjuda && <AjudaTreball pestanya="contactes" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${activeTab === "desvincular" ? "is-danger" : ""}`} aria-hidden="true">
            {activeTab === "desvincular" ? "🗑️" : "👥"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titolContactes}</h1>
            <p className="dv2-pageHeader-subtitle">{subtitol}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title="Com funcionen els Contactes del Treball"
            aria-label="Ajuda"
            onClick={() => setMostraAjuda(true)}
          >
            ?
          </button>
          <button
            type="button"
            className="dv2-btn"
            onClick={
              activeTab === "table"
                ? onCancel
                : () => {
                    setActiveTab("table");
                    setError("");
                    setDups(null);
                  }
            }
          >
            Tornar
          </button>
          {activeTab === "table" && potCrear && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={obrirNou}>
              Nou contacte
            </button>
          )}
          {activeTab === "nou" && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={idExistent ? handleVincularExistent : () => handleCrear(false)}
              disabled={enviant}
            >
              {idExistent ? "Vincular" : "Desar"}
            </button>
          )}
          {activeTab === "modificar" && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleGuardarRelacio} disabled={enviant}>
              Desar
            </button>
          )}
          {activeTab === "consultar" && potModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => obrirFitxa(selected, "modificar")}>
              Modificar
            </button>
          )}
          {activeTab === "consultar" && potModificar && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      setActiveTab("desvincular");
                    }}
                  >
                    Desvincular
                  </button>
                </div>
              )}
            </div>
          )}
          {activeTab === "desvincular" && (
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleDesvincular} disabled={enviant}>
              Desvincular
            </button>
          )}
        </div>
      </div>
      {subnav}

      {activeTab === "table" ? (
        <div className="dv2-entity-body">
          {error ? <p className="dv2-hint">{error}</p> : null}
          {renderTaula()}
        </div>
      ) : (
        renderFitxa()
      )}

      {dups && (
        <div className="dv2-modalOverlay" role="dialog" aria-modal="true">
          <div className="dv2-modal">
            <h3 className="dv2-modal-title">Possibles contactes duplicats</h3>
            <p className="dv2-modal-lead">
              S'han trobat coincidències. Pots reutilitzar un contacte existent o crear-ne un de nou.
            </p>
            <ul className="dv2-specList" style={{ marginBottom: 12 }}>
              {dups.map((c) => (
                <li key={c.idContactes} className="dv2-spec">
                  <span className="dv2-spec-name">
                    {nomContacte(c)} — {c.Telefon || "—"} — {c.Email || "—"}
                  </span>
                  <button
                    type="button"
                    className="dv2-btn dv2-btn--compact"
                    onClick={() => {
                      setDups(null);
                      setIdExistent(String(c.idContactes));
                      omplirDesDeExistent(c.idContactes);
                      enviarVincle({ idContactes: c.idContactes });
                    }}
                  >
                    Usar
                  </button>
                </li>
              ))}
            </ul>
            <div className="dv2-modal-actions">
              <button type="button" className="dv2-btn" onClick={() => setDups(null)}>
                Cancel·la
              </button>
              <button
                type="button"
                className="dv2-btn dv2-btn--primary"
                onClick={() => {
                  setDups(null);
                  handleCrear(true);
                }}
              >
                Crear de nou
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactesTreball;
